import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type IValuesModal from './interfaces/IValuesModal';

import styles from './scss/ValuesModal.module.scss';

function ValuesModal({
  isVisible,
  values,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: IValuesModal) {
  const [modalValues, setModalValues] = useState<string[]>([]);
  const [position, setPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);

  const handleShowMoreMouseEnter = () => {
    const formattedValues = values
      .split('", "')
      .map((value) => value.replace(/^"|"$/g, '').trim())
      .filter((value) => value.length > 0);

    setModalValues(formattedValues);
    onMouseEnter?.();
  };

  const handleShowMoreMouseLeave = () => {
    setModalValues([]);
    onMouseLeave?.();
  };

  const handleShowMoreClick = () => {
    const formattedValues = values
      .split('", "')
      .map((value) => value.replace(/^"|"$/g, '').trim())
      .filter((value) => value.length > 0);

    setModalValues(formattedValues);
    onClick?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleShowMoreClick();
    }
  };

  useLayoutEffect(() => {
    if (triggerRef.current && isVisible) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isVisible]);

  return (
    <>
      <span className={styles.showMoreWrapper}>
        <span
          ref={triggerRef}
          className={styles.showMoreText}
          onMouseEnter={onClick ? undefined : handleShowMoreMouseEnter}
          onMouseLeave={onClick ? undefined : handleShowMoreMouseLeave}
          onClick={onClick ? handleShowMoreClick : undefined}
          onKeyDown={onClick ? handleKeyDown : undefined}
          tabIndex={0}
          role="button"
          aria-label="Show more values"
        >
          Show more
        </span>
      </span>
      {isVisible && modalValues.length > 0 && typeof document !== 'undefined'
        ? createPortal(
          <div
            className={styles.valuesModal}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              zIndex: 10001,
            }}
            onMouseEnter={onClick ? undefined : onMouseEnter}
            onMouseLeave={onClick ? undefined : onMouseLeave}
          >
            <div className={styles.valuesModalContent}>
              {modalValues.map((value) => (
                <div key={value} className={styles.valueItem}>
                  {value}
                </div>
              ))}
            </div>
          </div>,
          document.body,
        ) : null}
    </>
  );
}

export default ValuesModal;
